import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { getDefaultStore, useAtomValue } from "jotai";

import { graphql } from "@repo/graphql-types/gql";

import { RecurrenceCreateHeader } from "@/components/recurring-appointment/recurrence-create-header";
import { RecurrenceReview } from "@/components/recurring-appointment/recurrence-review";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { useGraphQL } from "@/hooks/use-graphql";
import {
  recurringAppointmentFormAtom,
  recurringSessionsAtom,
  recurringSlotAtom,
} from "@/lib/atoms/recurring-appointment-form-atom";
import { getFrequencyLabel } from "@/lib/helpers/recurrence";
import { differenceInMinutes } from "@/lib/time";

import type { JSX } from "react";

const RecurringAppointmentReviewPageQuery = graphql(/* GraphQL */ `
  query RecurringAppointmentReviewPageQuery {
    utilizacaoRecorrencia {
      maximoAgendamentosPorBloco
      disponivel
    }
  }
`);

const RecurringAppointmentReviewPage = (): JSX.Element => {
  const queryResult = useGraphQL(RecurringAppointmentReviewPageQuery, {});
  const { data } = queryResult;

  const form = useAtomValue(recurringAppointmentFormAtom);
  const slot = useAtomValue(recurringSlotAtom);
  const navigate = useNavigate();

  const duration =
    slot.horaInicio && slot.horaFim
      ? differenceInMinutes({ startTime: slot.horaInicio, endTime: slot.horaFim })
      : 0;

  const subtitle = [
    form.unitAcronym,
    `${form.appointmentTypeName} – ${duration} min`,
    getFrequencyLabel(form.frequency),
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <RoundedLayout>
      <RecurrenceCreateHeader
        title="Revisar recorrência"
        subtitle={subtitle}
        onEdit={() => void navigate({ to: "/recurring-appointments/create" })}
      />
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data?.utilizacaoRecorrencia ? (
            <RecurrenceReview
              maxPerBlock={data.utilizacaoRecorrencia.maximoAgendamentosPorBloco}
            />
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/recurring-appointments/create/review")({
  component: RecurringAppointmentReviewPage,
  beforeLoad: () => {
    const store = getDefaultStore();
    const slot = store.get(recurringSlotAtom);
    const sessions = store.get(recurringSessionsAtom);

    if (!slot.horaInicio || sessions.length === 0) {
      redirect({ to: "/recurring-appointments/create", throw: true });
    }
  },
});
